import { AutoSuggestAddress as KiamaAutoSuggestAddress } from '@snsw/react-component-library';
import React, { ReactNode, useMemo, useRef, useState } from 'react';
import { useMount, useUpdate } from 'react-use';
import { useAutoSuggestAddressBackendApi } from './kiama/useAddressSearchBackendApi';
import { PropertyNameField } from './PropertyNameField';
import {
  AutoSuggestAddressApi,
  AutoSuggestAddressEntryMode,
  AutoSuggestAddressModel,
  AutoSuggestAddressType,
} from './types';
import { AddressSuggestion } from './kiama/types';

export interface AutoSuggestAddressProps {
  /** Unique ID used to prefix the form fields */
  id: string;

  /** Label to show above the input component */
  label: ReactNode;

  /** Help message to show to provide further context to the user */
  helpMessage?: ReactNode;

  /** The initial entry mode to show to the user. Defaults to `auto`. */
  initialMode?: AutoSuggestAddressEntryMode;

  /** The value to show in the control. Use the `formattedAddress` to default the label. */
  value?: AutoSuggestAddressModel;

  /** Callback invoked when the user enters a new value in the component. */
  onChange: (address: AutoSuggestAddressModel, changedPropertyName?: string, isValidated?: boolean) => void;

  /** Callback invoked when the user selects an entry from the autocomplete list. */
  onSelect?: (address: AutoSuggestAddressModel, changedPropertyName: string, isValidated: boolean) => void;

  /** Callback invoked when the user removes focus from the component. */
  onBlur?: (address: AutoSuggestAddressModel, changedPropertyName: string, isValidated: boolean) => void;

  /** Callback invoked when the entry mode switches between auto/manual */
  onEntryModeChange?: (entryMode: AutoSuggestAddressEntryMode) => void;

  /** The API to use for address lookup */
  api: AutoSuggestAddressApi;

  /** Restricts the address types to use when searching for addresses */
  addressType?: AutoSuggestAddressType;

  /** The minimum number of characters required to be entered before triggering an address search. Defaults to `8`. */
  minSearchLength?: number;

  /** The amount of time (in milliseconds) to wait after the input changes before searching. Defaults to `1000`. */
  debounceMillis?: number;

  /** Additional properties to be applied for manual address mode */
  manualAddressProps?: {
    disablePropertyNameField?: boolean;
  };
}

/**
 * Creates a default suggestion to be displayed when the user has entered more characters than Kiama needs to do an
 * address search, but not enough characters for our API.
 */
const useDefaultSuggestion = (minSearchLength: number): AddressSuggestion =>
  useMemo(
    () => ({
      id: '__CUSTOM',
      rank: 1,
      suggestion: `Please enter ${minSearchLength} characters to search for an address.`,
      isCustom: true,
      onClick: () => {
        // We have to throw here to prevent the Kiama AutoSuggest control from clearing out the input.  We are purposely
        // throwing a non-error (since you can throw anything in JS) to distinguish this from an actual error.
        //
        // See `handleOnSelect` in @snsw/react-component-library/src/Components/AutoSuggest/AutoSuggest.js
        throw {
          message: 'The address does not exist.',
        };
      },
    }),
    [minSearchLength]
  );

export const AutoSuggestAddress = ({
  id,
  api,
  addressType,
  minSearchLength = 8,
  debounceMillis = 1000,
  initialMode = 'auto',
  onEntryModeChange,
  value,
  onChange,
  onSelect,
  manualAddressProps,
  ...componentProps
}: AutoSuggestAddressProps) => {
  const [entryMode, setEntryMode] = useState(initialMode);

  // Cache of fully selected auto-mode addresses (those that carry a `nswPointId`), keyed by any text that can
  // identify them (the suggestion text the user picked and the resulting formatted address). The underlying snsw
  // control does not re-fire `onSelect` when the user re-selects an already-selected suggestion id, and it drops the
  // selection when switching entry modes; this cache lets us re-emit the complete address in those cases so the
  // consumer keeps a valid selection.
  const selectedAddressCache = useRef<Map<string, AutoSuggestAddressModel>>(new Map());
  const lastChangeTextRef = useRef<string>('');

  const rememberSelectedAddress = (keys: (string | null | undefined)[], address: AutoSuggestAddressModel) => {
    const cache = selectedAddressCache.current;

    keys
      .filter((key): key is string => Boolean(key))
      .forEach((key) => {
        // Re-insert so the most recently selected addresses are treated as the newest entries.
        cache.delete(key);
        cache.set(key, address);
      });
  };

  const handleChange = (
    // The active entry mode. Mode-switch callers pass the new mode explicitly, since the state hasn't updated yet
    // during the synchronous onChange that follows a mode switch.
    mode: AutoSuggestAddressEntryMode,
    address: AutoSuggestAddressModel,
    changedPropertyName?: string,
    isValidated?: boolean
  ) => {
    const text = address?.formattedAddress?.value ?? '';
    lastChangeTextRef.current = text;

    // When the text matches a previously selected address but the payload has lost its `nswPointId` (because the
    // control didn't re-fire `onSelect` for the same suggestion id, or dropped the value during a mode switch),
    // re-emit the complete cached address. This is routed through `onSelect` (not `onChange`) so consumers that
    // treat text changes as an invalidation of the selection still keep the restored `nswPointId`.
    if (mode === 'auto' && !address?.nswPointId?.value && text) {
      const cachedAddress = selectedAddressCache.current.get(text);
      if (cachedAddress?.nswPointId?.value) {
        onSelect
          ? onSelect(cachedAddress, 'formattedAddress', true)
          : onChange(cachedAddress, 'formattedAddress', true);
        return;
      }
    }

    onChange(address, changedPropertyName, isValidated);
  };

  const handleSelect = (address: AutoSuggestAddressModel, changedPropertyName: string, isValidated: boolean) => {
    if (address?.nswPointId?.value) {
      rememberSelectedAddress([lastChangeTextRef.current, address.formattedAddress?.value], address);
    }

    onSelect && onSelect(address, changedPropertyName, isValidated);
  };

  const defaultSuggestion = useDefaultSuggestion(minSearchLength);
  const backendApi = useAutoSuggestAddressBackendApi(
    api,
    addressType,
    minSearchLength,
    debounceMillis,
    defaultSuggestion
  );

  const containerRef = useRef<HTMLDivElement>();
  const forceRender = useUpdate();

  // If the component started in manual input mode, we need to force a rerender after mounting - since `containerRef`
  // will only get initialised after the first render, the first render of <PropertyNameField /> will have been given
  // an undefined container to create its portal in, and hence it will not have rendered.
  const showPropertyNameField = entryMode === 'manual' && !manualAddressProps?.disablePropertyNameField;
  useMount(() => showPropertyNameField && forceRender());

  const handleEntryModeChange = (entryMode: AutoSuggestAddressEntryMode) => {
    onEntryModeChange && onEntryModeChange(entryMode);

    setEntryMode(entryMode);
    if (!value) return;

    // Convert the manual address to an auto address (or vice-versa)
    if (entryMode === 'auto') {
      const { propertyName, streetAddress, suburb, state, postcode } = value;
      handleChange(entryMode, {
        ...value,
        formattedAddress: {
          value: [propertyName?.value, streetAddress?.value, suburb?.value, state?.value, postcode?.value]
            .map((s) => s?.trim())
            .filter((s) => !!s)
            .join(' '),
        },
      });
    } else {
      const { streetAddress, formattedAddress } = value;
      handleChange(entryMode, {
        ...value,
        streetAddress: {
          value: streetAddress?.value ?? formattedAddress?.value,
        },
      });
    }
  };

  const propertyName = value?.propertyName;

  const handlePropertyNameChange = (next: string) =>
    onChange({
      ...value,
      propertyName: {
        value: next,
        errorMessage: propertyName?.errorMessage,
      },
    });

  return (
    <div ref={containerRef}>
      {showPropertyNameField && (
        <PropertyNameField
          ownerId={id}
          value={propertyName?.value ?? ''}
          onChange={handlePropertyNameChange}
          errorMessage={propertyName?.errorMessage}
          parentContainer={containerRef.current}
        />
      )}

      <KiamaAutoSuggestAddress
        {...componentProps}
        id={id}
        value={value}
        onChange={(address, changedPropertyName, isValidated) =>
          handleChange(entryMode, address, changedPropertyName, isValidated)
        }
        onSelect={handleSelect}
        initialMode={initialMode}
        onSwitch={handleEntryModeChange}
        backendAPI={backendApi}
      />
    </div>
  );
};
